import { Star } from 'lucide-react'

import { formatRating } from '@/lib/broker-format'
import { cmp, money, sub, type Money } from '@/lib/money'
import { REVIEW_MAX_RATING } from '@/schemas/broker'
import { cn } from '@/lib/utils'

const SIZES = {
  // 14 → size-3 (4-grid): the tie-break's usual size-4 (16px) would land sm
  // on the same size as `md`.
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
} as const

interface StarRatingProps {
  /**
   * Out of 5, as the API sends it — a branded `Money` decimal string ("4.4")
   * for a house's average, or a plain integer for a single review's own
   * rating. Null means nobody has rated this house yet, which is not the
   * same as zero.
   */
  value: Money | number | null
  /**
   * `"stars"` (default): a row of 5 icons, each full/half/empty-filled by a
   * Decimal comparison against `value` — the By-broker header, the reviews
   * rail's big rating, and each review card.
   *
   * `"compact"`: a single star glyph beside the figure, inline with other
   * text — the All-brokers row's "★ 4.4  Broker 33" run, where the design
   * draws one glyph, not five.
   */
  variant?: 'stars' | 'compact'
  /**
   * Prints the figure beside the stars. Ignored in `"compact"`, which always
   * shows the figure — that number IS the compact form. Off by default in
   * `"stars"`: the reviews rail draws its own big rating number separately
   * and only wants the glyph row from this component.
   */
  showValue?: boolean
  size?: keyof typeof SIZES
  className?: string
}

const HALF = money('0.5')

/**
 * How full the nth star is: 'full', 'half' or 'empty'.
 *
 * Compared with Decimal rather than floats — 4.4 is not representable in
 * IEEE-754 and `4.4 - 4 >= 0.5` is the sort of comparison that flips a glyph
 * on one machine and not another.
 */
const fillOf = (value: Money, index: number): 'full' | 'half' | 'empty' => {
  const filled = money(String(index))
  const remainder = sub(value, filled)
  if (cmp(remainder, money('1')) >= 0) return 'full'
  if (cmp(remainder, HALF) >= 0) return 'half'
  return 'empty'
}

const NULL_LABEL = 'Not rated yet'

/**
 * The rating figure and its stars, in the two shapes Broker Chirfaar draws.
 *
 * Needed in at least five places across the feature — All brokers' rows,
 * this broker's header, Compare, the Reviews page's title and rail, and
 * every review card — so it is one component, not five. It previously
 * existed twice (`screens/brokers/components/all-brokers/StarRating.tsx` and
 * `.../reviews/StarRating.tsx`), one per Wave 2 agent that needed it and
 * could not reach `src/components/ui/` without touching another agent's
 * files. The two disagreed on the null copy ("No rating yet" vs. "Not rated
 * yet" — neither is Figma-mandated, both are invented for a state Figma
 * never draws a sample of; this merge keeps the latter) and, more
 * significantly, on the value's own precision: the reviews copy printed it
 * via `formatMoney` ("4.40"), which is wrong per the spec above and is the
 * bug this merge fixes, not a difference to preserve.
 *
 * `text-rating-star` (the mock's `--rating-star`, br:kit), not a market tone:
 * a rating is a judgement about a trading house, not a price going up. And not
 * `text-warning`, which turned blue (2026-10-26): a star is amber, and a
 * rating is not a caution. The whole control carries an `aria-label` that
 * states the rating in words, so the stars are never the only way to read it.
 */
export default function StarRating({
  value,
  variant = 'stars',
  showValue = false,
  size = 'md',
  className,
}: StarRatingProps) {
  if (value === null) {
    return <span className={cn('text-muted-foreground', className)}>{NULL_LABEL}</span>
  }

  const rating = typeof value === 'number' ? money(String(value)) : value
  const label = `${formatRating(rating)} out of ${String(REVIEW_MAX_RATING)}`

  if (variant === 'compact') {
    return (
      <span className={cn('inline-flex items-center gap-1', className)} aria-label={label}>
        <Star
          className={cn(SIZES[size], 'shrink-0 fill-rating-star text-rating-star')}
          aria-hidden="true"
        />
        <span className="tnum">{formatRating(rating)}</span>
      </span>
    )
  }

  return (
    // 6 → gap-2 (4-grid, nearest-8 tie-break).
    <span className={cn('inline-flex items-center gap-2', className)} aria-label={label}>
      {showValue ? (
        <span className="tnum text-label-l text-foreground">{formatRating(rating)}</span>
      ) : null}
      {/* 2 → gap-1 (4-grid): the tie-break's nearest-8 pick is 0, which would
          run the five stars edge to edge. */}
      <span aria-hidden="true" className="inline-flex items-center gap-1">
        {Array.from({ length: REVIEW_MAX_RATING }, (_, index) => {
          const fill = fillOf(rating, index)
          return (
            <span key={index} className="relative inline-flex">
              <Star className={cn(SIZES[size], 'text-border-strong')} />
              {fill === 'empty' ? null : (
                // A half star is the same glyph clipped to its left half, so
                // the two halves can never drift apart by a sub-pixel.
                <span
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: fill === 'half' ? '50%' : '100%' }}
                >
                  <Star className={cn(SIZES[size], 'fill-current text-rating-star')} />
                </span>
              )}
            </span>
          )
        })}
      </span>
    </span>
  )
}
